import { useEffect, useRef } from 'react';
import type { ChatToolCatalogItem, ChatToolSet } from '@pm-next/shared-types';
import { cn } from '@/lib/utils';
import { groupComposerTools } from './composer-mentions';

interface ChatMentionListProps {
  kind: 'tool' | 'set';
  tools: ChatToolCatalogItem[];
  toolSets: ChatToolSet[];
  activeIndex: number;
  onHover: (index: number) => void;
  onSelectTool: (tool: ChatToolCatalogItem) => void;
  onSelectSet: (toolSet: ChatToolSet) => void;
}

export function ChatMentionList({
  kind,
  tools,
  toolSets,
  activeIndex,
  onHover,
  onSelectTool,
  onSelectSet,
}: ChatMentionListProps) {
  const activeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  if (kind === 'set') {
    return (
      <div
        className="max-h-64 overflow-y-auto rounded-xl border bg-popover p-1 shadow-lg"
        role="listbox"
        aria-label="Tool sets"
      >
        {toolSets.length === 0 ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">No matching tool sets</p>
        ) : (
          toolSets.map((toolSet, index) => (
            <button
              key={toolSet.id}
              ref={index === activeIndex ? activeRef : undefined}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => onHover(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                onSelectSet(toolSet);
              }}
              className={cn(
                'flex w-full flex-col items-start gap-0.5 rounded-lg px-3 py-2 text-left',
                index === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/70'
              )}
            >
              <span className="text-sm font-medium">/{toolSet.id}</span>
              <span className="text-xs text-muted-foreground">{toolSet.description}</span>
            </button>
          ))
        )}
      </div>
    );
  }

  const groups = groupComposerTools(tools);
  let offset = 0;

  return (
    <div
      className="max-h-64 overflow-y-auto rounded-xl border bg-popover p-1 shadow-lg"
      role="listbox"
      aria-label="Tools"
    >
      {tools.length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">No matching tools</p>
      ) : (
        groups.map((group) => {
          const start = offset;
          offset += group.tools.length;
          return (
            <div key={group.category} className="mb-1 last:mb-0">
              <p className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {group.label}
              </p>
              {group.tools.map((tool, groupIndex) => {
                const index = start + groupIndex;
                return (
                  <button
                    key={tool.name}
                    ref={index === activeIndex ? activeRef : undefined}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    onMouseEnter={() => onHover(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onSelectTool(tool);
                    }}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 rounded-lg px-3 py-2 text-left',
                      index === activeIndex
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-muted/70'
                    )}
                  >
                    <span className="text-sm font-medium">{tool.label}</span>
                    <span className="text-xs text-muted-foreground">{tool.summary}</span>
                    <span className="font-mono text-[10px] text-muted-foreground/80">
                      @{tool.name}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
}
